import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './FaceDiagnosisPage.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import type { AxiosError } from 'axios';
import ImageCapture from './ImageCapture';
import apiClient from '../services/apiClient';
import { analyzeImageReport } from '../services/imageReportService';
import { CameraFocus, ImageQualityLabel, ImageQualityMetrics, ImageReportAnalyzeResponse, ImageReportDetail } from '../types';

type CaptureFocus = CameraFocus;
type CameraPermissionState = 'unknown' | 'prompt' | 'granted' | 'denied' | 'unsupported';

const QUALITY_LABEL_TEXT: Record<ImageQualityLabel, string> = {
  excellent: '质量优秀',
  acceptable: '质量可用',
  needs_retake: '建议重拍',
};

const FaceDiagnosisPage = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [latestReport, setLatestReport] = useState<ImageReportDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [qualityResult, setQualityResult] = useState<ImageQualityMetrics | null>(null);
  const [qualityOverride, setQualityOverride] = useState<boolean>(false);
  
  // 摄像头相关状态
  const [isCameraOpen, setIsCameraOpen] = useState<boolean>(false);
  const [isCameraReady, setIsCameraReady] = useState<boolean>(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [captureFocus, setCaptureFocus] = useState<CaptureFocus>('face');
  const [copyStatus, setCopyStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [cameraPermission, setCameraPermission] = useState<CameraPermissionState>('unknown');
  const [permissionBannerDismissed, setPermissionBannerDismissed] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const readinessTimeoutRef = useRef<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // 清理预览URL的副作用
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      // 关闭摄像头流
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (readinessTimeoutRef.current) {
        window.clearTimeout(readinessTimeoutRef.current);
        readinessTimeoutRef.current = null;
      }
    };
  }, [previewUrl, stream]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraPermission('unsupported');
      return;
    }

    let cancelled = false;
    let permissionStatus: PermissionStatus | null = null;
    let statusChangeHandler: (() => void) | null = null;

    const mapPermission = (state: PermissionState): CameraPermissionState => {
      if (state === 'granted') return 'granted';
      if (state === 'denied') return 'denied';
      return 'prompt';
    };

    const attachPermissionListener = async () => {
      if (!navigator.permissions || !(navigator.permissions as any).query) {
        setCameraPermission('prompt');
        return;
      }

      try {
        const status = await (navigator.permissions as any).query({ name: 'camera' });
        if (cancelled) {
          return;
        }

        permissionStatus = status;
        const update = () => {
          const mapped = mapPermission(status.state);
          setCameraPermission(mapped);
          if (mapped === 'granted') {
            setPermissionBannerDismissed(true);
          }
        };

        update();
        statusChangeHandler = update;
        status.addEventListener('change', update);
      } catch (err) {
        if (!cancelled) {
          setCameraPermission('prompt');
        }
      }
    };

    attachPermissionListener();

    return () => {
      cancelled = true;
      if (permissionStatus && statusChangeHandler) {
        permissionStatus.removeEventListener('change', statusChangeHandler);
      }
    };
  }, []);

  useEffect(() => {
    if (cameraPermission === 'denied') {
      setPermissionBannerDismissed(false);
    }
  }, [cameraPermission]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      // 检查文件大小（限制为5MB）
      if (file.size > 5 * 1024 * 1024) {
        setError('图片大小不能超过5MB。');
        setSelectedFile(null);
        setPreviewUrl(null);
        return;
      }

      // 检查文件类型
      if (!file.type.startsWith('image/')) {
        setError('请选择图片文件。');
        setSelectedFile(null);
        setPreviewUrl(null);
        return;
      }

  setSelectedFile(file);
  setPreviewUrl(URL.createObjectURL(file));
  setError(null);
  setLatestReport(null);
  setQualityResult(null);
  setQualityOverride(false);
    }
  };

  const handleClearImage = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setError(null);
    setLatestReport(null);
    setQualityResult(null);
    setQualityOverride(false);
  };

  const handleQualityUpdate = (result: ImageQualityMetrics | null) => {
    setQualityResult(result);
    if (!result || result.quality_label !== 'needs_retake') {
      setQualityOverride(false);
    }
  };

  const resolveAssetPath = useCallback((assetUrl: string): string => {
    let normalized = assetUrl.trim();
    if (normalized.startsWith('/')) {
      normalized = normalized.slice(1);
    }
    if (normalized.startsWith('api/')) {
      normalized = normalized.slice(4);
    }
    return normalized;
  }, []);

  const openAssetInNewTab = useCallback(
    async (assetUrl: string) => {
      if (/^https?:\/\//i.test(assetUrl)) {
        window.open(assetUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      const requestPath = resolveAssetPath(assetUrl);
      try {
        const response = await apiClient.get<Blob>(requestPath, { responseType: 'blob' });
        const objectUrl = URL.createObjectURL(response.data);
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      } catch (fetchError) {
        console.error('打开影像资源失败', fetchError);
        setError('影像资源加载失败，请稍后重试。');
      }
    },
    [resolveAssetPath]
  );

  const buildFullAssetUrl = useCallback((assetPath?: string | null) => {
    if (!assetPath) {
      return undefined;
    }
    if (/^https?:\/\//i.test(assetPath)) {
      return assetPath;
    }
    let normalized = assetPath.trim();
    if (normalized.startsWith('/')) {
      normalized = normalized.slice(1);
    }
    if (normalized.startsWith('api/')) {
      normalized = normalized.slice(4);
    }
    const baseUrl = (apiClient.defaults.baseURL || '').replace(/\/+$/, '');
    if (!baseUrl) {
      return `/${normalized}`;
    }
    return `${baseUrl}/${normalized}`;
  }, []);

  const formatMetadataValue = useCallback((value: unknown): string => {
    if (value === null || value === undefined) {
      return '—';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch (err) {
      return String(value);
    }
  }, []);

  const heatmapKey = latestReport?.heatmap_url ?? null;
  const heatmapPreviewUrl = useMemo(
    () => buildFullAssetUrl(heatmapKey),
    [buildFullAssetUrl, heatmapKey]
  );

  const metadataPayload = latestReport?.metadata ?? null;
  const metadataEntries = useMemo(() => {
    if (!metadataPayload) {
      return [] as Array<[string, unknown]>;
    }
    return Object.entries(metadataPayload);
  }, [metadataPayload]);

  const primaryMarkdown = useMemo(() => {
    if (!latestReport) {
      return null;
    }
    return latestReport.summary_md ?? latestReport.analysis ?? null;
  }, [latestReport]);

  const reportQuality = latestReport?.quality ?? qualityResult;

  // 打开摄像头
  const openCamera = async () => {
    console.debug('[face-diagnosis] openCamera invoked', { permission: cameraPermission });
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('当前浏览器不支持摄像头调用，请尝试更换浏览器或更新版本。');
        setCameraPermission('unsupported');
        return;
      }

      setIsCameraReady(false);
      const constraints = { video: { width: 720, height: 480, facingMode: 'user' } };
      console.debug('[face-diagnosis] requesting user media', constraints);
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      console.debug(
        '[face-diagnosis] stream acquired',
        mediaStream.getTracks().map((track) => `${track.kind}:${track.label}`),
      );
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      setStream(mediaStream);
      setError(null);
      setCameraPermission('granted');
      setPermissionBannerDismissed(true);
      setIsCameraOpen(true);
    } catch (err) {
      const error = err as any;
      let errorMessage = '无法访问摄像头，请检查权限设置。';
      if (error?.name === 'NotAllowedError') {
        errorMessage = '摄像头访问被拒绝，请在浏览器中允许摄像头权限。';
        setCameraPermission('denied');
      } else if (error?.name === 'NotFoundError') {
        errorMessage = '未找到可用的摄像头设备。';
      } else if (error?.name === 'NotReadableError') {
        errorMessage = '摄像头被其他应用占用或硬件错误。';
      } else if (error?.name === 'OverconstrainedError') {
        errorMessage = '摄像头不支持请求的配置。';
      }
      setError(errorMessage);
      console.error('[face-diagnosis] openCamera failed', err);
    }
  };

  // 关闭摄像头
  const closeCamera = () => {
    if (stream) {
      console.debug('[face-diagnosis] closing previous stream');
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (readinessTimeoutRef.current) {
      window.clearTimeout(readinessTimeoutRef.current);
      readinessTimeoutRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
    setIsCameraOpen(false);
    setIsCameraReady(false);
  };

  const handleCameraToggle = async () => {
    if (isCameraOpen) {
      closeCamera();
      return;
    }
    setPermissionBannerDismissed(true);
    await openCamera();
  };

  useEffect(() => {
    if (!isCameraOpen || !stream) {
      return;
    }

    const video = videoRef.current;
    if (!video) {
      console.debug('[face-diagnosis] camera open but video element unavailable');
      return;
    }

    console.debug('[face-diagnosis] attaching stream to video element');
    setIsCameraReady(false);
    video.srcObject = stream;
    video.muted = true;
    video.setAttribute('playsinline', 'true');

    const markReady = () => {
      console.debug('[face-diagnosis] video element signaled readiness');
      setIsCameraReady(true);
    };

    video.addEventListener('canplay', markReady, { once: true });
    video.addEventListener('loadeddata', markReady, { once: true });

    const startPlayback = async () => {
      try {
        const playResult = video.play();
        if (playResult && typeof playResult.then === 'function') {
          await playResult;
        }
        console.debug('[face-diagnosis] video playback promise resolved');
      } catch (playError) {
        console.warn('摄像头视频播放被阻止，将尝试使用静态帧。', playError);
      }
    };

    void startPlayback();

    if (readinessTimeoutRef.current) {
      window.clearTimeout(readinessTimeoutRef.current);
    }
    readinessTimeoutRef.current = window.setTimeout(() => {
      if (stream.active) {
        console.debug('[face-diagnosis] readiness fallback fired, stream active:', stream.active);
        setIsCameraReady(true);
      }
      readinessTimeoutRef.current = null;
    }, 800);

    return () => {
      video.removeEventListener('canplay', markReady);
      video.removeEventListener('loadeddata', markReady);
      if (readinessTimeoutRef.current) {
        window.clearTimeout(readinessTimeoutRef.current);
        readinessTimeoutRef.current = null;
      }
    };
  }, [isCameraOpen, stream]);

  // 拍照
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;

      if (!isCameraReady) {
        setError('摄像头还未准备就绪，请稍等...');
        return;
      }

      if (video.videoWidth === 0 || video.videoHeight === 0) {
        setError('摄像头视频尺寸异常，请重新打开摄像头...');
        return;
      }

      if (video.readyState < 2) {
        setError('摄像头数据未就绪，请稍等片刻...');
        // 尝试触发视频播放
        video.play().catch(console.error);
        return;
      }

      const context = canvas.getContext('2d');

      if (context) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

        // 将canvas转换为blob，然后创建File对象
        canvas.toBlob((blob) => {
          if (blob) {
            console.debug('[face-diagnosis] capture success', {
              width: video.videoWidth,
              height: video.videoHeight,
              size: blob.size,
            });
            const timestamp = new Date().getTime();
            const file = new File([blob], `camera-capture-${timestamp}.jpg`, { type: 'image/jpeg' });
            setSelectedFile(file);

            // 创建预览URL
            if (previewUrl) {
              URL.revokeObjectURL(previewUrl);
            }
            const newPreviewUrl = URL.createObjectURL(file);
            setPreviewUrl(newPreviewUrl);
            setLatestReport(null);
            
            closeCamera();
            setError(null);
          } else {
            console.debug('[face-diagnosis] capture failed, empty blob');
            setError('拍照失败，请重试');
          }
        }, 'image/jpeg', 0.9);
      }
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError('请选择一张图片进行上传。');
      return;
    }

    if (qualityResult?.quality_label === 'needs_retake' && !qualityOverride) {
      setError('影像质控未通过，请重新拍摄或勾选“仍要提交”继续。');
      return;
    }

    setLoading(true);
    setLatestReport(null);
    setError(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('focus', captureFocus);

    if (qualityOverride) {
      formData.append('force_accept', 'true');
    }

    try {
      const response: ImageReportAnalyzeResponse = await analyzeImageReport(formData);
      setLatestReport(response.report ?? null);
      setQualityResult(response.quality ?? null);
      setQualityOverride(false);
    } catch (err) {
      const axiosErr = err as AxiosError<{ detail?: any }>;
      if (axiosErr.response?.status === 422) {
        const detail = axiosErr.response.data?.detail ?? {};
        const detailQuality = (detail?.quality ?? null) as ImageQualityMetrics | null;
        if (detailQuality) {
          setQualityResult(detailQuality);
        }
        const message =
          (typeof detail?.message === 'string' && detail.message) || '影像质控未通过，请重新采集。';
        setError(message);
      } else {
        setError('影像采集分析失败，请稍后再试。');
      }
      setLatestReport(null);
    } finally {
      setLoading(false);
    }
  };

  const focusConfig = useMemo(() => ({
    face: {
      label: '面部轮廓',
      subtitle: '确保正对镜头、光线均匀，露出全脸五官',
      tips: [
        '保持脸部正视镜头，眼睛与镜头平行。',
        '避免逆光，选择柔和光线或自然光。',
        '放松表情，保持自然状态。'
      ],
  prompt: '请根据我的面部正面照，从气色、眼周、鼻翼、唇色等维度给出中医影像辨证分析与调理建议。'
    },
    tongue: {
      label: '舌苔特写',
      subtitle: '轻微伸舌，聚焦舌面与舌苔细节，避免抖动',
      tips: [
        '选择明亮的自然光源或白光灯。',
        '舌头放松自然伸出，保持稳定。',
        '确保舌面完全进入画面，避免遮挡。'
      ],
      prompt: '这里是舌头的特写照片，请根据舌色、舌苔厚薄与湿度给出证候判断与饮食调护建议。'
    },
    skin: {
      label: '皮肤局部',
      subtitle: '用于观察局部色素、红疹或干燥部位，保持清晰对焦',
      tips: [
        '选择关注的皮肤区域进行近距离拍摄。',
        '确保皮肤表面清洁无化妆。',
        '保持镜头稳定，必要时借助支架或他人协助。'
      ],
      prompt: '这是一张皮肤局部照片，请结合颜色、质地和可能的炎症情况，从中医角度进行辨证分析并提供调理建议。'
    }
  }), []);

  const heroStats = useMemo(
    () => [
      { label: '支持捕捉模式', value: '3 种' },
      { label: '当前重点', value: focusConfig[captureFocus].label },
  { label: 'AI诊断状态', value: latestReport ? '已生成' : loading ? '诊断中' : '待启动' }
    ],
  [captureFocus, focusConfig, latestReport, loading]
  );

  const qualityGateActive = qualityResult?.quality_label === 'needs_retake' && !qualityOverride;

  const handleFocusSelect = (focus: CaptureFocus) => {
    setCaptureFocus(focus);
    setCopyStatus('idle');
  };

  const handleCopyPrompt = async () => {
    const promptText = focusConfig[captureFocus].prompt;
    try {
      if (!navigator.clipboard || !window.isSecureContext) {
        throw new Error('clipboard not available');
      }
      await navigator.clipboard.writeText(promptText);
      setCopyStatus('success');
    } catch (err) {
      setCopyStatus('error');
    } finally {
      setTimeout(() => setCopyStatus('idle'), 2000);
    }
  };

  const ensureEncodedReportId = (path: string): string => {
    return path.replace(/(image-reports\/)([^/?#]+)/, (_, prefix, id) => {
      return `${prefix}${encodeURIComponent(id)}`;
    });
  };

  const normalizeInternalReportUrl = (raw: string, fallbackId: string): string => {
    const trimmed = raw.replace(/^\s+|\s+$/g, '');
    if (!trimmed) {
      return `/app/image-reports/${fallbackId}`;
    }

    if (trimmed.startsWith('/')) {
      if (/^\/app\//.test(trimmed)) {
        return ensureEncodedReportId(trimmed);
      }
      if (/^\/image-reports\//.test(trimmed)) {
        return ensureEncodedReportId(`/app${trimmed}`);
      }
      if (/^\/reports\//.test(trimmed)) {
        return ensureEncodedReportId(`/app/image-reports/${trimmed.slice(9)}`);
      }
      return ensureEncodedReportId(`/app${trimmed}`);
    }

    if (/^app\//.test(trimmed)) {
      return ensureEncodedReportId(`/${trimmed}`);
    }

    if (/^image-reports\//.test(trimmed)) {
      return ensureEncodedReportId(`/app/${trimmed}`);
    }

    if (/^reports\//.test(trimmed)) {
      return ensureEncodedReportId(`/app/image-reports/${trimmed.slice(8)}`);
    }

    if (/^api\/image-reports\//.test(trimmed)) {
      return ensureEncodedReportId(`/app/${trimmed.slice(4)}`);
    }

    if (/^api\/reports\//.test(trimmed)) {
      return ensureEncodedReportId(`/app/image-reports/${trimmed.slice(12)}`);
    }

    return ensureEncodedReportId(`/app/image-reports/${trimmed}`);
  };

  const openReportDetail = (report: ImageReportDetail) => {
    if (!report) {
      console.warn('[face-diagnosis] openReportDetail called without report payload');
      return;
    }

  const safeReportId = encodeURIComponent(String(report.report_id));
  const fallbackPath = `/app/image-reports/${safeReportId}`;
  const reportUrl = (report.report_url || '').trim();
    console.debug('[face-diagnosis] openReportDetail payload', {
      reportUrl,
      fallbackPath,
      locationHref: window.location.href,
    });

    if (reportUrl) {
      if (/^https?:\/\//i.test(reportUrl)) {
        window.open(reportUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      const normalized = normalizeInternalReportUrl(reportUrl, safeReportId);
      console.debug('[face-diagnosis] navigating to normalized path', normalized);
      navigate(normalized);
      return;
    }

    console.debug('[face-diagnosis] navigating to fallback path', fallbackPath);
    navigate(fallbackPath);
  };

  return (
    <motion.div
      className="unified-page-wrapper face-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="unified-page-header face-hero">
        <div className="face-hero-text">
          <span className="face-hero-badge">AI Assisted TCM</span>
          <h2>AI影像采集中心</h2>
          <p className="face-hero-sub">
            借助视觉AI与经典望诊经验，捕捉面部、舌苔、皮肤细节，获得个性化的整体体质评估与调理建议。
          </p>
          <div className="face-hero-tags">
            <span>真人标准引导</span>
            <span>AI 辨证要点</span>
            <span>多模态采集</span>
          </div>
        </div>
        <div className="face-hero-stats">
          {heroStats.map((stat) => (
            <div key={stat.label} className="face-hero-stat-card">
              <span className="stat-value">{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="unified-page-content-scrollable face-scroll">
        <div className="face-layout">
          <div className="capture-column">
            <motion.div
              className="capture-setup-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="panel-header">
                <h3>采集模式</h3>
                <span className="panel-caption">根据所需诊断重点选择合适的影像模式</span>
              </div>

              {cameraPermission === 'unsupported' && (
                <div className="camera-permission-banner unsupported" role="alert">
                  <h4>当前环境不支持摄像头</h4>
                  <p>检测到当前浏览器或设备不提供摄像头访问能力。请改用带有摄像头的设备，或在桌面端使用最新版本的 Chrome、Edge 浏览器。</p>
                </div>
              )}

              {(cameraPermission === 'prompt' || cameraPermission === 'unknown') && !permissionBannerDismissed && (
                <div className="camera-permission-banner prompt" role="status">
                  <div className="banner-copy">
                    <h4>需要授权摄像头</h4>
                    <p>允许后即可实时采集影像，辅助 AI 影像辨证做出更准确的诊断分析。</p>
                  </div>
                  <div className="banner-actions">
                    <button
                      type="button"
                      className="permission-primary"
                      onClick={handleCameraToggle}
                    >
                      立即授权
                    </button>
                    <button
                      type="button"
                      className="permission-secondary"
                      onClick={() => setPermissionBannerDismissed(true)}
                    >
                      稍后再说
                    </button>
                  </div>
                </div>
              )}

              {cameraPermission === 'denied' && (
                <div className="camera-permission-banner denied" role="alert">
                  <div className="banner-copy">
                    <h4>摄像头权限已被关闭</h4>
                    <p>请在浏览器地址栏的权限设置中将摄像头访问改为“允许”，然后刷新页面或重新点击授权。</p>
                  </div>
                  <ol className="permission-steps">
                    <li>点击浏览器地址栏左侧的锁状图标或摄像头图标。</li>
                    <li>找到“摄像头”设置并选择“允许”。</li>
                    <li>刷新页面后再次点击“启用实时取像”。</li>
                  </ol>
                  <button
                    type="button"
                    className="permission-primary ghost"
                    onClick={handleCameraToggle}
                  >
                    我已调整，重新检查
                  </button>
                </div>
              )}

              <div className="capture-focus-selector">
                {(
                  [
                    { value: 'face', title: '面部', desc: '面色气血' },
                    { value: 'tongue', title: '舌苔', desc: '寒热虚实' },
                    { value: 'skin', title: '皮肤', desc: '局部对症' }
                  ] as Array<{ value: CaptureFocus; title: string; desc: string }>
                ).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`focus-chip ${captureFocus === option.value ? 'active' : ''}`}
                    onClick={() => handleFocusSelect(option.value)}
                  >
                    <span>{option.title}</span>
                    <small>{option.desc}</small>
                  </button>
                ))}
              </div>

              <p className="capture-focus-sub">{focusConfig[captureFocus].subtitle}</p>

              <div className="input-actions">
                <label htmlFor="file-upload" className="custom-file-upload">
                  <span className="upload-icon">�</span>
                  {selectedFile ? selectedFile.name : '选择图片文件'}
                </label>
                <input id="file-upload" type="file" accept="image/*" onChange={handleFileChange} />

                <button
                  onClick={handleCameraToggle}
                  className={`camera-button ${isCameraOpen ? 'active' : ''}`}
                  disabled={cameraPermission === 'denied' || cameraPermission === 'unsupported'}
                  type="button"
                  title={
                    cameraPermission === 'denied'
                      ? '请先在浏览器设置中允许摄像头权限'
                      : cameraPermission === 'unsupported'
                      ? '当前环境无法启用摄像头'
                      : undefined
                  }
                >
                  <span className="camera-icon">📷</span>
                  {isCameraOpen ? '关闭摄像头' : '启用实时取像'}
                </button>
              </div>

              {isCameraOpen && (
                <div className="camera-wrapper">
                  <div className="camera-status">
                    {isCameraReady ? (
                      <span className="status-ready">摄像头已准备就绪</span>
                    ) : (
                      <span className="status-loading">摄像头准备中...</span>
                    )}
                  </div>
                  <video ref={videoRef} autoPlay playsInline muted className="camera-video" />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                  <button
                    onClick={capturePhoto}
                    className="capture-button"
                    disabled={!isCameraReady}
                    title={isCameraReady ? '点击拍照' : '请等待摄像头准备完成'}
                  >
                    📸 {isCameraReady ? '捕捉影像' : '准备中...'}
                  </button>
                </div>
              )}
            </motion.div>

            <motion.div
              className="capture-guidance-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="guidance-header">
                <h3>AI诊前提示</h3>
                <span className="panel-caption">通过准确影像让AI诊断更贴近临床</span>
              </div>
              <ul className="guidance-list">
                {focusConfig[captureFocus].tips.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
              <button
                type="button"
                className={`copy-prompt-button ${copyStatus}`}
                onClick={handleCopyPrompt}
              >
                {copyStatus === 'success' && '已复制至剪贴板'}
                {copyStatus === 'error' && '复制失败，请手动复制'}
                {copyStatus === 'idle' && '复制AI问诊提示'}
              </button>
              <p className="prompt-hint">复制后可在智能问诊对话中粘贴，辅助AI结合影像进行综合分析。</p>
            </motion.div>
          </div>

          <motion.div
            className="preview-column"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <div className="preview-card">
              <div className="preview-header">
                <h3>影像预览</h3>
                {selectedFile && <span className="file-name">{selectedFile.name}</span>}
              </div>
              <ImageCapture
                focus={captureFocus}
                file={selectedFile}
                previewUrl={previewUrl}
                onClear={handleClearImage}
                onQualityUpdate={handleQualityUpdate}
              />

              <div className="diagnosis-actions">
                {qualityResult && (
                  <div className={`quality-indicator ${qualityResult.quality_label}`}>
                    <div className="quality-indicator-row">
                      <span className="quality-label-text">
                        当前质控：{QUALITY_LABEL_TEXT[qualityResult.quality_label]}
                      </span>
                    </div>
                    <div className="quality-indicator-metrics">
                      <span>清晰度 {qualityResult.clarity_score.toFixed(1)}</span>
                      <span>光照 {qualityResult.lighting_score.toFixed(1)}</span>
                      <span>构图 {qualityResult.composition_score.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {qualityGateActive && (
                  <div className="quality-gate">
                    <p>AI 检测到影像质量不足，建议重新采集后再尝试诊断。</p>
                    <label>
                      <input
                        type="checkbox"
                        checked={qualityOverride}
                        onChange={(event) => setQualityOverride(event.target.checked)}
                      />
                      仍要提交（我已确认影像质量）
                    </label>
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={!selectedFile || loading || qualityGateActive}
                  className="diagnosis-button"
                >
                  {loading ? <span className="spinner"></span> : '开始诊断'}
                </button>
                {error && <p className="error-message inline">{error}</p>}
              </div>

              {loading && (
                <div className="loading-overlay">
                  <div className="loading-text">影像采集中...</div>
                  <div className="loading-spinner"></div>
                </div>
              )}
            </div>

            {latestReport && (
              <div className="diagnosis-result-card">
                <h3>诊断结果</h3>
                <p className="diagnosis-summary">
                  AI 已为当前影像生成 <strong>{latestReport.focus_label}</strong> 分析报告。
                </p>

                {reportQuality && (
                  <div className="report-quality-hints">
                    <h4>质控说明</h4>
                    {reportQuality.issues?.length ? (
                      <ul className="quality-issue-tags">
                        {reportQuality.issues.map((issue) => (
                          <li key={issue}>{issue}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="quality-positive">影像质量达标，AI 已按标准流程生成诊断建议。</p>
                    )}
                    {reportQuality.suggestion && (
                      <p className="quality-suggestion">{reportQuality.suggestion}</p>
                    )}
                  </div>
                )}

                {latestReport.structured_report && (
                  <div className="report-structured">
                    <h4>AI 辨证要点</h4>
                    {latestReport.structured_report.conclusion && (
                      <p className="structured-conclusion">
                        {latestReport.structured_report.conclusion}
                      </p>
                    )}
                    {latestReport.structured_report.evidence?.length ? (
                      <div className="structured-section">
                        <h5>识别依据</h5>
                        <ul>
                          {latestReport.structured_report.evidence.map((item, index) => (
                            <li key={`evidence-${index}`}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {latestReport.structured_report.recommendations?.length ? (
                      <div className="structured-section">
                        <h5>调护建议</h5>
                        <ul>
                          {latestReport.structured_report.recommendations.map((item, index) => (
                            <li key={`recommendation-${index}`}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {latestReport.structured_report.risks?.length ? (
                      <div className="structured-section">
                        <h5>风险提示</h5>
                        <ul>
                          {latestReport.structured_report.risks.map((item, index) => (
                            <li key={`risk-${index}`}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                )}

                {heatmapPreviewUrl && (
                  <div className="report-heatmap-preview">
                    <div className="report-heatmap-header">
                      <h4>AI 热力图</h4>
                      <button
                        type="button"
                        className="report-action-button ghost"
                        onClick={() => {
                          if (latestReport.heatmap_url) {
                            openAssetInNewTab(latestReport.heatmap_url);
                          }
                        }}
                      >
                        新窗口打开
                      </button>
                    </div>
                    <img src={heatmapPreviewUrl} alt="AI 热力图预览" />
                  </div>
                )}

                {primaryMarkdown && (
                  <div className="report-markdown markdown-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                      {primaryMarkdown}
                    </ReactMarkdown>
                  </div>
                )}

                <div className="report-action-buttons">
                  {latestReport.image_url && (
                    <button
                      type="button"
                      className="report-action-button"
                      onClick={() => {
                        if (latestReport.image_url) {
                          openAssetInNewTab(latestReport.image_url);
                        }
                      }}
                    >
                      查看原始影像
                    </button>
                  )}
                  {latestReport.heatmap_url && !heatmapPreviewUrl && (
                    <button
                      type="button"
                      className="report-action-button"
                      onClick={() => {
                        if (latestReport.heatmap_url) {
                          openAssetInNewTab(latestReport.heatmap_url);
                        }
                      }}
                    >
                      查看 AI 热力图
                    </button>
                  )}
                </div>

                {metadataEntries.length > 0 && (
                  <details className="report-metadata">
                    <summary>采集元数据</summary>
                    <div className="metadata-grid">
                      {metadataEntries.map(([key, value]) => {
                        const formatted = formatMetadataValue(value);
                        const isMultiline = formatted.includes('\n');
                        return (
                          <div key={key} className="metadata-item">
                            <span className="metadata-key">{key}</span>
                            {isMultiline ? (
                              <code className="metadata-value metadata-code">{formatted}</code>
                            ) : (
                              <span className="metadata-value">{formatted}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </details>
                )}

                <div className="report-detail-cta">
                  <div className="report-detail-meta">
                    <span className="report-focus-tag">{latestReport.focus_label}</span>
                    {latestReport.created_at && (
                      <span className="report-timestamp">
                        {new Date(latestReport.created_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="report-detail-button"
                    onClick={() => openReportDetail(latestReport)}
                  >
                    查看报告详情
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default FaceDiagnosisPage;
